import React, { useContext, useEffect, useState } from "react";
import { CheckCircle2, Clock3, ExternalLink, RefreshCcw, Search, XCircle } from "lucide-react";
import { AuthContext } from "../AuthContext";
import { API_BASE_URL } from "../api";
import { Button, SectionHeader, SelectField, StatusPill, SurfaceCard, TextareaField } from "../components/designSystem";

const durationOptions = ["1m", "3m", "6m", "12m"];

function formatDate(value) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString();
}

function formatAmount(amount, currency) {
  if (!Number.isFinite(Number(amount))) {
    return "Manual review";
  }

  if ((currency || "NGN").toUpperCase() === "NGN") {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount));
  }

  return `${currency || "USD"} ${amount}`;
}

function statusTone(status) {
  if (status === "approved") return "success";
  if (status === "rejected") return "danger";
  if (status === "pending") return "warning";
  return "neutral";
}

export default function AdminPendingPayments() {
  const { token, user } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionState, setActionState] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredRequests = normalizedQuery
    ? requests.filter((entry) => {
        const request = entry.upgradeRequest || {};
        const searchText = [
          entry.username,
          entry.email,
          entry.plan,
          request.payerName,
          request.paymentReference,
          request.requestedPlan,
          request.requestedDuration,
          request.currency,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchText.includes(normalizedQuery);
      })
    : requests;

  useEffect(() => {
    if (!user?.isAdmin) {
      return;
    }

    async function loadRequests() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/upgrade-requests?status=pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Failed to fetch pending payment requests.");
        }

        setRequests(Array.isArray(data.users) ? data.users : []);
        setActionState((current) => {
          const next = { ...current };
          (Array.isArray(data.users) ? data.users : []).forEach((entry) => {
            next[entry._id] = next[entry._id] || {
              duration: entry.upgradeRequest?.requestedDuration || "1m",
              adminNote: "",
              loading: false,
            };
          });
          return next;
        });
      } catch (loadError) {
        setError(loadError.message || "Failed to fetch pending payment requests.");
      } finally {
        setLoading(false);
      }
    }

    loadRequests();
  }, [token, user]);

  async function reviewRequest(userId, decision) {
    const currentAction = actionState[userId] || { duration: "1m", adminNote: "", loading: false };
    setActionState((current) => ({
      ...current,
      [userId]: { ...currentAction, loading: true },
    }));
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/upgrade-request/${userId}/review`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          decision,
          duration: currentAction.duration,
          adminNote: currentAction.adminNote,
        }),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to review payment request.");
      }

      setRequests((current) => current.filter((entry) => entry._id !== userId));
    } catch (reviewError) {
      setError(reviewError.message || "Failed to review payment request.");
    } finally {
      setActionState((current) => ({
        ...current,
        [userId]: { ...currentAction, loading: false },
      }));
    }
  }

  if (!user?.isAdmin) {
    return <p className="mt-5 text-red-500">Access denied. Admins only.</p>;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <SurfaceCard>
        <SectionHeader
          eyebrow="Admin Payments"
          title="Pending payment approvals"
          description="Review bank-transfer submissions, inspect uploaded proof, then approve or reject without using the generic special-upgrade page."
          action={
            <Button type="button" variant="secondary" onClick={() => window.location.reload()}>
              <RefreshCcw className="h-4 w-4" />
              Refresh
            </Button>
          }
        />
        <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-md">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search by user, email, payer, ref, plan"
              className="w-full rounded-full border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-slate-400 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100 dark:focus:border-gray-500"
            />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Showing {filteredRequests.length} of {requests.length} requests</p>
        </div>
      </SurfaceCard>

      {error ? (
        <SurfaceCard>
          <p className="text-sm font-medium text-rose-600 dark:text-rose-300">{error}</p>
        </SurfaceCard>
      ) : null}

      {loading ? (
        <SurfaceCard>
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading pending requests...</p>
        </SurfaceCard>
      ) : null}

      {!loading && !requests.length ? (
        <SurfaceCard>
          <p className="text-sm text-slate-500 dark:text-slate-400">No pending payment requests right now.</p>
        </SurfaceCard>
      ) : null}

      {!loading && requests.length > 0 && filteredRequests.length === 0 ? (
        <SurfaceCard>
          <p className="text-sm text-slate-500 dark:text-slate-400">No requests match your search.</p>
        </SurfaceCard>
      ) : null}

      {!loading
        ? filteredRequests.map((entry) => {
            const draft = actionState[entry._id] || { duration: entry.upgradeRequest?.requestedDuration || "1m", adminNote: "", loading: false };
            const request = entry.upgradeRequest || {};
            const isImageProof = request.paymentProof?.type?.startsWith("image/");

            return (
              <SurfaceCard key={entry._id}>
                <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
                  <div className="space-y-4 xl:max-w-[58%]">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-xl font-semibold text-slate-950 dark:text-white">{entry.username}</h2>
                      <StatusPill tone={statusTone(request.status)}>{request.status || "pending"}</StatusPill>
                      <StatusPill tone="brand">{request.requestedPlan?.toUpperCase() || "REQUEST"}</StatusPill>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Account</p>
                        <p className="mt-3 text-sm font-medium text-slate-900 dark:text-white">{entry.email}</p>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Current plan: {entry.plan}</p>
                      </div>
                      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-400">Transfer</p>
                        <p className="mt-3 text-sm font-medium text-slate-900 dark:text-white">{request.paymentReference || "-"}</p>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Submitted {formatDate(request.submittedAt)}</p>
                      </div>
                    </div>
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm leading-7 text-slate-600 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-300">
                      <p><span className="font-semibold text-slate-950 dark:text-white">Payer name:</span> {request.payerName || "-"}</p>
                      <p><span className="font-semibold text-slate-950 dark:text-white">Duration:</span> {request.requestedDuration || "-"}</p>
                      <p><span className="font-semibold text-slate-950 dark:text-white">Amount:</span> {formatAmount(request.amount, request.currency)}</p>
                    </div>
                  </div>

                  <div className="w-full space-y-4 xl:max-w-[36%]">
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">Uploaded proof</p>
                        {request.paymentProof?.url ? (
                          <a href={request.paymentProof.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-sky-700 hover:text-sky-600 dark:text-sky-300 dark:hover:text-sky-200">
                            Open
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        ) : null}
                      </div>
                      {request.paymentProof?.url ? (
                        <div className="mt-4 space-y-3">
                          {isImageProof ? (
                            <img src={request.paymentProof.url} alt={request.paymentProof.name || "Payment proof"} className="h-56 w-full rounded-2xl object-cover" />
                          ) : (
                            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-300">
                              {request.paymentProof.name || "Proof document uploaded"}
                            </div>
                          )}
                          <p className="text-xs text-slate-500 dark:text-slate-400">{request.paymentProof.name || "Uploaded proof"}</p>
                        </div>
                      ) : (
                        <div className="mt-4 rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                          No proof file uploaded. Review is limited to the transfer reference.
                        </div>
                      )}
                    </div>

                    {request.requestedPlan ? (
                      <SelectField
                        label="Approval duration"
                        value={draft.duration}
                        onChange={(event) => setActionState((current) => ({
                          ...current,
                          [entry._id]: { ...draft, duration: event.target.value },
                        }))}
                      >
                        {durationOptions.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </SelectField>
                    ) : null}

                    <TextareaField
                      label="Admin note"
                      rows={4}
                      value={draft.adminNote}
                      onChange={(event) => setActionState((current) => ({
                        ...current,
                        [entry._id]: { ...draft, adminNote: event.target.value },
                      }))}
                      placeholder="Optional note for the user or internal review context"
                    />

                    <div className="flex flex-wrap gap-3">
                      <Button type="button" variant="brand" disabled={draft.loading} onClick={() => reviewRequest(entry._id, "approve")}>
                        <CheckCircle2 className="h-4 w-4" />
                        Approve
                      </Button>
                      <Button type="button" variant="danger" disabled={draft.loading} onClick={() => reviewRequest(entry._id, "reject")}>
                        <XCircle className="h-4 w-4" />
                        Reject
                      </Button>
                    </div>
                    <div className="inline-flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                      <Clock3 className="h-3.5 w-3.5" />
                      Approval here updates entitlement directly. Use the Admin Panel for manual special upgrades.
                    </div>
                  </div>
                </div>
              </SurfaceCard>
            );
          })
        : null}
    </div>
  );
}